import Phaser from "phaser";

import { ONBOARDING_ART } from "../config/artAssets";

export type GuidePose = keyof typeof ONBOARDING_ART.keeper;

export type OnboardingGuidePresentation = {
  pose: GuidePose;
  mirror: boolean;
  reducedMotion: boolean;
  compact: boolean;
};

export type OnboardingGuideCallbacks = {
  onReady?(): void;
};

export type OnboardingGuideSnapshot = {
  pose: GuidePose;
  mirror: boolean;
  x: number;
  y: number;
  angle: number;
  scaleY: number;
};

const POSES = Object.keys(ONBOARDING_ART.keeper) as GuidePose[];

export class OnboardingGuideScene extends Phaser.Scene {
  readonly #callbacks: OnboardingGuideCallbacks;
  readonly #renderScale: number;
  #presentation: OnboardingGuidePresentation;
  #width = 0;
  #height = 0;
  #baseY = 0;
  #activePose: GuidePose | null = null;
  #character!: Phaser.GameObjects.Container;
  #images = {} as Record<GuidePose, Phaser.GameObjects.Image>;
  #sparks: Phaser.GameObjects.Arc[] = [];
  #guideShards: Phaser.GameObjects.Polygon[] = [];
  #ambientTweens: Phaser.Tweens.Tween[] = [];

  constructor(
    callbacks: OnboardingGuideCallbacks,
    presentation: OnboardingGuidePresentation,
    renderScale: number,
  ) {
    super({ key: "OnboardingGuide" });
    this.#callbacks = callbacks;
    this.#presentation = presentation;
    this.#renderScale = renderScale;
  }

  preload(): void {
    for (const pose of POSES) {
      const asset = ONBOARDING_ART.keeper[pose];
      this.load.image(asset.key, asset.url);
    }
  }

  create(): void {
    this.cameras.main.setBackgroundColor("rgba(0,0,0,0)");
    this.cameras.main.transparent = true;
    for (const pose of POSES) {
      this.#images[pose] = this.add.image(0, 0, ONBOARDING_ART.keeper[pose].key).setVisible(false);
    }
    this.#character = this.add.container(0, 0, POSES.map((pose) => this.#images[pose]));
    this.#sparks = Array.from({ length: 12 }, (_, index) =>
      this.add
        .circle(0, 0, index % 4 === 0 ? 2.2 : 1.3, index % 3 === 0 ? 0xffd282 : 0x7df7ef, 0)
        .setBlendMode(Phaser.BlendModes.ADD),
    );
    this.#guideShards = Array.from({ length: 4 }, (_, index) =>
      this.add
        .polygon(0, 0, [0, -6, 4, 0, 0, 6, -4, 0], index % 2 === 0 ? 0xffdd91 : 0x6ff7eb, 0)
        .setBlendMode(Phaser.BlendModes.ADD)
        .setVisible(false),
    );
    this.scale.on(Phaser.Scale.Events.RESIZE, this.#layout, this);
    this.#layout({ width: this.scale.width, height: this.scale.height } as Phaser.Structs.Size);
    this.#showPose(false);
    this.#restartAmbientMotion();
    this.#callbacks.onReady?.();
  }

  setPresentation(presentation: OnboardingGuidePresentation): void {
    const poseChanged = presentation.pose !== this.#presentation.pose;
    const layoutChanged = presentation.compact !== this.#presentation.compact;
    const mirrorChanged = presentation.mirror !== this.#presentation.mirror;
    const motionChanged = presentation.reducedMotion !== this.#presentation.reducedMotion;
    if (!poseChanged && !layoutChanged && !mirrorChanged && !motionChanged) return;
    this.#presentation = presentation;
    if (this.sys.isActive()) {
      if (layoutChanged) this.#layout({ width: this.scale.width, height: this.scale.height } as Phaser.Structs.Size);
      if (poseChanged) this.#showPose(true);
      else if (mirrorChanged) this.#applyImageGeometry();
      if (motionChanged && !layoutChanged && !poseChanged) this.#restartAmbientMotion();
    }
  }

  celebrate(): void {
    if (!this.sys.isActive() || this.#presentation.reducedMotion) return;
    const centreX = this.#width / 2;
    const centreY = this.#height * 0.43;
    this.#sparks.forEach((spark, index) => {
      const angle = Phaser.Math.DegToRad(-155 + index * (310 / Math.max(1, this.#sparks.length - 1)));
      const distance = 72 + (index % 3) * 17;
      spark.setPosition(centreX, centreY).setAlpha(0.9).setScale(0.7).setVisible(true);
      this.tweens.add({
        targets: spark,
        x: centreX + Math.cos(angle) * distance,
        y: centreY + Math.sin(angle) * distance,
        alpha: 0,
        scale: 1.6,
        duration: 720,
        delay: index * 24,
        ease: "Cubic.easeOut",
        onComplete: () => spark.setVisible(false),
      });
    });
  }

  debugSnapshot(): OnboardingGuideSnapshot {
    return {
      pose: this.#presentation.pose,
      mirror: this.#presentation.mirror,
      x: this.#character.x,
      y: this.#character.y,
      angle: this.#character.angle,
      scaleY: this.#character.scaleY,
    };
  }

  #layout(size: Phaser.Structs.Size): void {
    const physicalWidth = Math.round(size.width);
    const physicalHeight = Math.round(size.height);
    const width = Math.round(physicalWidth / this.#renderScale);
    const height = Math.round(physicalHeight / this.#renderScale);
    if (width <= 0 || height <= 0) return;
    if (width === this.#width && height === this.#height) return;
    this.#width = width;
    this.#height = height;
    this.cameras.main
      .setViewport(0, 0, physicalWidth, physicalHeight)
      .setZoom(this.#renderScale)
      .centerOn(width / 2, height / 2);
    this.#baseY = height - (this.#presentation.compact ? 1 : 5);
    this.#character.setPosition(width / 2, this.#baseY);
    this.#applyImageGeometry();
    this.#restartAmbientMotion();
  }

  #applyImageGeometry(): void {
    for (const pose of POSES) {
      const image = this.#images[pose];
      const asset = ONBOARDING_ART.keeper[pose];
      const fit = this.#presentation.compact
        ? Math.min((this.#height * 0.98) / asset.height, (this.#width * 0.98) / asset.width)
        : Math.min((this.#height * 0.98) / asset.height, (this.#width * 0.94) / asset.width);
      image
        .setPosition(0, 0)
        .setOrigin(0.5, 1)
        .setFlipX(false)
        .setScale(this.#presentation.mirror ? -fit : fit, fit);
    }
  }

  #showPose(animatePose: boolean): void {
    const nextPose = this.#presentation.pose;
    const next = this.#images[nextPose];
    this.tweens.killTweensOf(Object.values(this.#images));
    this.#applyImageGeometry();
    for (const pose of POSES) {
      const image = this.#images[pose];
      if (pose !== nextPose && (!animatePose || pose !== this.#activePose)) image.setVisible(false).setAlpha(0);
    }
    next.setVisible(true).setX(0);
    if (animatePose && !this.#presentation.reducedMotion && this.#activePose !== null) {
      const previous = this.#images[this.#activePose];
      previous.setVisible(true).setAlpha(1);
      next.setAlpha(0).setX(0);
      this.tweens.add({
        targets: previous,
        alpha: 0,
        duration: 180,
        ease: "Sine.easeOut",
        onComplete: () => previous.setVisible(false).setX(0),
      });
      this.tweens.add({
        targets: next,
        alpha: 1,
        duration: 260,
        ease: "Sine.easeOut",
      });
    } else {
      next.setAlpha(1).setX(0);
    }
    this.#activePose = nextPose;
    this.#restartAmbientMotion();
  }

  #restartAmbientMotion(): void {
    this.#ambientTweens.forEach((tween) => tween.stop());
    this.#ambientTweens = [];
    this.tweens.killTweensOf(this.#character);
    this.tweens.killTweensOf(this.#guideShards);
    this.#character.setPosition(this.#width / 2, this.#baseY).setAngle(0).setScale(1);
    this.#guideShards.forEach((shard) => shard.setVisible(false).setAlpha(0));
  }
}
